import { Controller } from "@hotwired/stimulus";

// This controller validates that image URLs are working and have the correct dimensions
export default class extends Controller {
  static targets = [
    "imageUrl",
    "placementField",
    "errorMessage",
    "imagePreview",
    "placeholder",
    "sizeDescription",
  ];

  static values = {
    skipValidation: {
      type: Boolean,
      default: false,
    },
    placements: {
      type: Array,
      default: [],
    },
  };

  connect() {
    console.log("Ad Image Validator", this.placementsValue);

    // Add event listener for URL field changes
    this.imageUrlTarget.addEventListener("blur", this.validateImage.bind(this));

    // Initial validation if there's already a URL
    if (this.imageUrlTarget.value) {
      this.validateImage();
      this.showImagePreview();
    }
  }

  disableValidationIfNeeded(event) {
    const { removedElement } = event.detail;
    console.log("Removed element", removedElement);

    if (removedElement.dataset.adId === this.element.dataset.adId) {
      console.log("Disabling validation for ad", removedElement.dataset.adId);
      this.skipValidationValue = true;
    }
  }

  displaySizeDescription() {
    console.log("Displaying size description");
    const placement = this.placementsValue.find(
      (placement) => placement.placement === this.placementFieldTarget.value
    );
    console.log("Placement", placement);

    if (placement) {
      this.sizeDescriptionTarget.textContent = placement.size;
    }
  }

  validateImage() {
    if (this.skipValidationValue) {
      return;
    }

    const imageUrl = this.imageUrlTarget.value.trim();

    // Clear any previous errors
    this.errorMessageTarget.style.display = "none";
    this.errorMessageTarget.textContent = "";

    // Don't validate empty URLs
    if (!imageUrl) {
      this.hideImagePreview();
      return;
    }

    // Format URL if needed
    const formattedUrl = this.formatUrl(imageUrl);
    if (formattedUrl !== imageUrl) {
      this.imageUrlTarget.value = formattedUrl;
    }

    // Test if image exists and can be loaded
    const img = new Image();
    img.onload = () => this.handleImageLoaded(img);
    img.onerror = () => this.handleImageError();
    img.src = formattedUrl;
  }

  formatUrl(url) {
    // If URL doesn't start with http:// or https://, add https://
    if (url && !url.match(/^https?:\/\//i)) {
      return "https://" + url;
    }
    return url;
  }

  showImagePreview() {
    const size = this.currentSize();

    if (!size) {
      return;
    }

    console.log("Current Size", size);
    const [width, height] = size.split("x");

    this.imagePreviewTarget.src = this.imageUrlTarget.value;
    this.imagePreviewTarget.width = width;
    this.imagePreviewTarget.height = height;
    this.imagePreviewTarget.style.display = "block";
    this.placeholderTarget.style.display = "none";
  }

  selectedPlacement() {
    return this.placementsValue.find(
      (placement) => placement.placement === this.placementFieldTarget.value
    );
  }

  currentSize() {
    const placement = this.selectedPlacement();
    if (!placement) {
      return;
    }

    return placement.size;
  }

  handleImageLoaded(img) {
    // Update preview image
    this.imagePreviewTarget.src = img.src;
    this.imagePreviewTarget.style.display = "block";
    this.placeholderTarget.style.display = "none";

    // Check dimensions based on selected size (if needed)

    const selectedSize = this.currentSize();

    const [width, height] = selectedSize.split("x").map((s) => parseInt(s, 10));

    // Provide warnings if image dimensions don't match expected size
    if (img.width !== width || img.height !== height) {
      this.showError(
        `Warning: Image dimensions (${img.width}x${img.height}) don't match the selected size (${width}x${height}). The image might appear distorted.`
      );
    }
  }

  handleImageError() {
    this.hideImagePreview();
    this.showError(
      "Unable to load the image. Please check the URL and try again."
    );
  }

  hideImagePreview() {
    this.imagePreviewTarget.style.display = "none";
    this.placeholderTarget.style.display = "block";
  }

  showError(message) {
    this.errorMessageTarget.textContent = message;
    this.errorMessageTarget.style.display = "block";
  }
}
